import React from "react";
import { Link } from "react-router-dom";
import { Send, Gamepad2, Star, Flame, Users, Rocket } from "lucide-react";
import { useData } from "../data/DataContext";


export const Corners = ({ color = "border-[#ffb224]/70", inside = false, size = "w-5 h-5" }) => (
  <>
    <span aria-hidden="true" className={`pointer-events-none absolute ${inside ? "top-1.5 left-1.5 group-hover:top-1 group-hover:left-1 group-active:top-1 group-active:left-1" : "-top-1.5 -left-1.5 group-hover:-top-2.5 group-hover:-left-2.5"} ${size} border-t-2 border-l-2 rounded-tl-md ${color} transition-all duration-200 z-10`} />
    <span aria-hidden="true" className={`pointer-events-none absolute ${inside ? "bottom-1.5 right-1.5 group-hover:bottom-1 group-hover:right-1 group-active:bottom-1 group-active:right-1" : "-bottom-1.5 -right-1.5 group-hover:-bottom-2.5 group-hover:-right-2.5"} ${size} border-b-2 border-r-2 rounded-br-md ${color} transition-all duration-200 z-10`} />
  </>
);


const Card = ({ game, pos, tag }) => (
  <Link
    to={`/game/${game.slug}`}
    data-testid={`hero-card-${game.slug}`}
    className={`group absolute w-[42%] sm:w-[40%] aspect-[3/4] rounded-[18px] sm:rounded-3xl p-[2px] bg-gradient-to-b from-[#ffb224]/70 via-[#1c5f35] to-[#ffb224]/30 shadow-[0_24px_50px_-18px_rgba(0,0,0,0.85)] transition-all duration-500 hover:z-30 hover:scale-105 hover:rotate-0 active:scale-[0.97] ${pos}`}
  >
    <span className="relative block w-full h-full rounded-[16px] sm:rounded-[22px] overflow-hidden bg-[#073c1b]">
      <Corners inside color="border-[#ffb224]" size="w-4 h-4" />
      <img src={game.logo} alt={`${game.name} Apk`} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
      <span className="absolute inset-0 bg-gradient-to-t from-[#01200c] via-[#013013]/35 to-transparent" />
      <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 -left-full w-1/2 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-[-20deg] group-hover:left-[150%] transition-[left] duration-700 ease-out" />
      {tag && (
        <span className="absolute top-2 left-2 sm:top-3 sm:left-3 flex items-center gap-1 bg-[#ffb224] text-[#062a13] text-[10px] sm:text-[11px] font-extrabold px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full shadow-lg">
          <Flame size={11} fill="currentColor" /> {tag}
        </span>
      )}
      <span className="absolute bottom-2 left-2 right-2 sm:bottom-3 sm:left-3 sm:right-3 bg-[#013013]/60 backdrop-blur-md border border-white/10 rounded-xl px-2 py-1.5 sm:px-2.5 sm:py-2">
        <span className="block text-white font-extrabold text-[12px] sm:text-[15px] leading-tight truncate">{game.name}</span>
        <span className="flex items-center gap-1 text-[#ffb224] text-[10px] sm:text-xs font-bold mt-0.5"><Star size={10} fill="currentColor" /> ₹{game.bonus} bonus</span>
      </span>
    </span>
  </Link>
);

const wrapWidth = (c) => (c.includes("sm:w-auto") ? "w-full sm:w-auto" : c.includes("w-full") ? "w-full" : "");


export const GoldButton = ({ children, className = "", ...props }) => (
  <span className={`group relative inline-flex ${wrapWidth(className)}`}>
    <Corners color="border-[#ffb224]/80" />
    <button
      {...props}
      className={`flex items-center justify-center gap-2 bg-[#ffb224] hover:bg-[#ffc14d] text-[#062a13] font-extrabold text-[15px] sm:text-lg px-5 sm:px-7 py-2.5 sm:py-3.5 rounded-xl sm:rounded-[14px] border-b-[4px] sm:border-b-[5px] border-[#b47400] shadow-[inset_0_2px_0_rgba(255,255,255,0.4)] active:border-b-[1px] active:translate-y-[4px] transition-all duration-150 ${className}`}
    >
      {children}
    </button>
  </span>
);

export const GhostButton = ({ children, className = "", ...props }) => (
  <span className={`group relative inline-flex ${wrapWidth(className)}`}>
    <Corners color="border-[#ffb224]/60" />
    <button
      {...props}
      className={`flex items-center justify-center gap-2 bg-[#15562e] hover:bg-[#1c6b3a] text-white font-extrabold text-[15px] sm:text-lg px-5 sm:px-7 py-2.5 sm:py-3.5 rounded-xl sm:rounded-[14px] border-b-[4px] sm:border-b-[5px] border-[#0a3a1b] shadow-[inset_0_2px_0_rgba(255,255,255,0.12)] active:border-b-[1px] active:translate-y-[4px] transition-all duration-150 ${className}`}
    >
      {children}
    </button>
  </span>
);

const Hero = ({ onExplore }) => {
  const { allGames, telegramLink: TELEGRAM_LINK, settings } = useData();
  const picks = [allGames[6] || allGames[0], allGames[5] || allGames[0], allGames[2] || allGames[0]].filter(Boolean);
  const c = settings.content || {};
  return (
  <section className="relative overflow-hidden">
    <div className="pointer-events-none absolute -top-40 -right-32 w-[520px] h-[520px] rounded-full bg-[#ffb224]/10 blur-[120px]" />
    <div className="relative max-w-6xl mx-auto px-4 pt-8 sm:pt-16 pb-8 sm:pb-10 grid lg:grid-cols-[1.1fr_0.9fr] gap-8 lg:gap-10 items-center">
      <div style={{ animation: "fadeUp 0.6s ease both" }}>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02] text-white">
          {c.heroTitle ? <>{c.heroTitle.split(" All ")[0]} All <span className="text-[#ffb224]">{c.heroTitle.split(" All ")[1] || "Yono Games."}</span></> : <>Play first. All <span className="text-[#ffb224]">Yono Games.</span></>}
        </h1>
        <p className="mt-4 sm:mt-5 text-emerald-50/75 text-[14px] sm:text-lg leading-relaxed max-w-xl">
          {c.heroLead || "Download every Yono app from one place - Rummy Zip, Jeet Spin, Gold Rummy, Yono 777, Jaiho 777, Spin 101 and 80 more. Each app gives a signup bonus from ₹49 to ₹545 with ₹100 minimum withdrawal, and every link here is checked before it goes live."}
        </p>
        <div className="mt-7 flex flex-col sm:flex-row gap-5 sm:gap-4">
          <a href={TELEGRAM_LINK} target="_blank" rel="noreferrer" data-testid="hero-telegram-btn" className="contents">
            <GoldButton className="w-full sm:w-auto"><Send size={18} /> Join Telegram</GoldButton>
          </a>
          <GhostButton data-testid="hero-explore-btn" onClick={onExplore} className="w-full sm:w-auto"><Gamepad2 size={18} /> All Yono Games</GhostButton>
        </div>
        <ul data-testid="hero-stats" className="mt-8 grid grid-cols-3 gap-2 sm:gap-3 max-w-lg">
          {[[Gamepad2, "90+", "games listed"], [Users, "25K+", "players daily"], [Rocket, "Weekly", "new drops"]].map(([Icon, v, l], i) => (
            <li
              key={l}
              className="group relative overflow-hidden flex flex-col items-start gap-2 bg-gradient-to-b from-[#0b4d24] to-[#052b14] border border-[#1c5f35] border-b-[4px] border-b-[#0a3a1b] rounded-2xl px-3 py-3 sm:px-4 sm:py-4 hover:border-[#ffb224]/60 hover:-translate-y-0.5 active:scale-[0.96] transition-all duration-200"
              style={{ animation: "fadeUp 0.5s ease both", animationDelay: `${250 + i * 80}ms` }}
            >
              <Corners inside size="w-3.5 h-3.5" color="border-[#ffb224]/70" />
              <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#ffb224] text-[#062a13] flex items-center justify-center shadow-[0_6px_14px_-6px_rgba(255,178,36,0.9)] border-b-[3px] border-[#b47400] group-hover:scale-110 transition-transform">
                <Icon size={18} strokeWidth={2.5} />
              </span>
              <strong className="block text-[#ffb224] text-xl sm:text-3xl font-extrabold leading-none">{v}</strong>
              <span className="text-emerald-50/70 text-[11px] sm:text-sm font-semibold leading-tight">{l}</span>
            </li>
          ))}
        </ul>
      </div>

      <div data-testid="hero-cards" className="relative h-[230px] sm:h-[330px] lg:h-[400px] max-w-[420px] sm:max-w-[520px] mx-auto w-full" style={{ animation: "fadeUp 0.7s ease both", animationDelay: "150ms" }}>
        {picks[0] && <Card game={picks[0]} pos="left-[3%] top-[10%] -rotate-[9deg] z-10" />}
        {picks[1] && <Card game={picks[1]} pos="right-[3%] top-[10%] rotate-[9deg] z-10" />}
        {picks[2] && <Card game={picks[2]} pos="left-1/2 -translate-x-1/2 top-0 z-20" tag="Trending" />}
      </div>
    </div>
  </section>
  );
};

export default Hero;
